import type { ComponentPropsWithoutRef, ElementType, Ref } from 'react';
import { cva } from 'class-variance-authority';
import type { BoardBackground } from '@molt/shared';
import { boardBackgroundStyle, isChosenBoardBackground } from '@/lib/board-backgrounds';
import { cn } from '@/lib/cn';

/**
 * The two opaque-or-board surfaces every card-shaped thing sits on
 * (`FE-04.10`; `docs/design/stage2/entry-surfaces.md`'s `CardSurface` row).
 *
 * `variant='sheet'` is an opaque `--color-surface-raised` at `--radius-card`
 * with `--shadow-card` — the surface `--color-ink-muted` is sent to, because
 * nothing translucent clears the 98% floor muted text needs.
 *
 * `variant='tile'` is the product's **second `--on-board` site**, after the
 * board face itself, and it obeys the identical rule `styles.css` states for
 * that first site: `data-board-text` is set from the background's
 * `textTreatment` **only when a background was actually chosen**.
 *
 * `boards.background_text_treatment` defaults to `'light'` and Paper's default
 * board is a light warm sheet, so a board nobody has touched is legally
 * `{ type: 'colour', colour: null }` with that column default still sitting
 * there unread. Honouring it would paint light text on a light board. A
 * stored default nobody picked is not a member's choice — so an untouched
 * tile carries NO `data-board-text` at all, and its ink follows the theme via
 * `--color-board`, exactly like the board face's own fallback.
 *
 * `FE-42.3` is where the actual fill becomes reachable. This primitive still
 * refuses arbitrary stored colour strings: it paints only the local palette
 * and curated stock photo refs that `board-backgrounds.ts` recognises, while
 * unknown values fall back to `--color-board` and keep the safe ink rule. The
 * `--text-shadow-on-board` halo the title needs is available the same way,
 * through the `text-shadow-on-board` utility `styles.css` registers — apply it
 * to the title text a caller renders inside.
 */

const cardSurfaceVariants = cva('rounded-card', {
  variants: {
    variant: {
      sheet: 'bg-surface-raised shadow-card',
      tile: 'overflow-hidden',
    },
    interactive: {
      true: 'cursor-pointer transition-shadow hover:shadow-card',
      false: '',
    },
  },
  defaultVariants: { interactive: false },
});

type CardSurfaceElement = 'div' | 'a' | 'button';

interface CardSurfaceOwnProps {
  variant: 'sheet' | 'tile';
  /** Element substitution — a board tile is commonly `as='a'`. */
  as?: CardSurfaceElement;
  interactive?: boolean;
  /**
   * `tile` only. Omitted, `null`, or the legal untouched row
   * (`{ type: 'colour', colour: null }`) all mean "no background was chosen"
   * — see the rule above.
   */
  background?: BoardBackground | null;
}

export type CardSurfaceProps = CardSurfaceOwnProps &
  Omit<
    ComponentPropsWithoutRef<'div'> &
      ComponentPropsWithoutRef<'a'> &
      ComponentPropsWithoutRef<'button'>,
    keyof CardSurfaceOwnProps
  > & {
    ref?: Ref<HTMLDivElement | HTMLAnchorElement | HTMLButtonElement>;
  };

export function CardSurface({
  as,
  variant,
  interactive = false,
  background = null,
  className,
  style,
  ref,
  ...rest
}: CardSurfaceProps) {
  /*
   * `as` is a union of three intrinsic tags, so TypeScript resolves `ref` to
   * the INTERSECTION of the three elements' ref types — a ref that is
   * simultaneously an anchor, a button and a div, which nothing can satisfy.
   * Widening `Component` to a plain component type collapses that intersection
   * back to the union the prop actually declares.
   *
   * The cast is confined to this one line and to the element identity; every
   * prop reaching it is still checked against the union above. Written out
   * because a bare `as any` here would look like the routine kind.
   */
  const Component = (as ?? 'div') as ElementType;

  const isChosen = variant === 'tile' && isChosenBoardBackground(background);

  const boardText = isChosen ? background?.textTreatment : undefined;
  const backgroundStyle =
    variant === 'tile' ? boardBackgroundStyle(background) : undefined;

  return (
    <Component
      ref={ref}
      data-board-text={boardText}
      data-board-background={isChosen ? '' : undefined}
      data-board-background-type={isChosen ? background?.type : undefined}
      className={cn(
        cardSurfaceVariants({ variant, interactive }),
        variant === 'tile' && !isChosen && 'bg-board',
        className,
      )}
      style={{ ...backgroundStyle, ...style }}
      {...rest}
    />
  );
}
